import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MixedText } from '../../utils/rtl';
import { Button } from './Button';
import SpaceBackground from '../layout/SpaceBackground';
import { useTheme } from '../layout/ThemeProvider';

interface ErrorMessageProps {
  className?: string;
  fullScreen?: boolean;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({ className = '', fullScreen = true }) => {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';
  const useDarkBackground = theme === 'dark' || theme === 'mixed';

  const handleGoBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div 
      className={`${fullScreen ? 'fixed inset-0 z-50' : 'relative'} flex items-center justify-center ${className}`}
      style={fullScreen ? { 
        background: useDarkBackground 
          ? 'linear-gradient(135deg, #1A1625 0%, #2A203C 100%)'
          : 'linear-gradient(135deg, #FAFAFA 0%, #F0F4F8 100%)'
      } : {}}
    >
      {/* Space Background */}
      {/* <SpaceBackground theme={theme} intensity="cosmic" /> */}
      
      {/* Background Stars */}
      {fullScreen && (
        <div className="absolute inset-0 overflow-hidden">
          {Array.from({ length: 50 }, (_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-vibrant-orange rounded-full animate-twinkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Error Content */}
      <div className="relative text-center z-10">
        {/* Dazed Astronaut */}
        <div className={`${fullScreen ? 'mb-8' : 'mb-4'} flex justify-center`}>
          <div className="relative">
            <img 
              src="/astronaut_error.png" 
              alt="Dazed Astronaut" 
              className={`${fullScreen ? 'w-32 h-32' : 'w-16 h-16'} object-contain animate-dazed-float filter drop-shadow-lg`}
              style={{
                filter: useDarkBackground 
                  ? 'drop-shadow(0 0 20px rgba(247, 103, 0, 0.4)) brightness(1.1)' 
                  : 'drop-shadow(0 0 15px rgba(26, 15, 43, 0.3))'
              }}
            />
            {/* Subtle glow ring around astronaut */}
            {fullScreen && (
              <div 
                className="absolute inset-0 rounded-full animate-pulse"
                style={{
                  background: `radial-gradient(circle, transparent 60%, ${useDarkBackground ? 'rgba(247, 103, 0, 0.2)' : 'rgba(123, 93, 153, 0.2)'} 100%)`,
                  pointerEvents: 'none'
                }}
              />
            )}
          </div>
        </div>
        
        <div className="space-y-2 max-w-md mx-auto px-6">
          <h2 className={`${fullScreen ? 'text-2xl' : 'text-lg'} font-bold font-hebrew text-center text-bg-primary`}>
            <MixedText>{t('errors:something_went_wrong')}</MixedText>
          </h2>
          
          {fullScreen && (
            <p className="text-lg font-hebrew text-center text-surface-secondary">
              <MixedText>{t('errors:our_part')}</MixedText>
            </p>
          )}

          {/* Go Back Button */}
          {fullScreen && (
            <div className="flex justify-center pt-6">
              <Button
                onClick={handleGoBack}
                variant="primary"
                size="lg"
                className="w-full max-w-xs"
              >
                <MixedText>{t('common:buttons.back')}</MixedText>
              </Button>
            </div>
          )}

          {/* Floating particles around text */}
          {fullScreen && (
            <div className="absolute inset-0 pointer-events-none">
              {Array.from({ length: 8 }, (_, i) => (
                <div
                  key={`particle-${i}`}
                  className="absolute w-0.5 h-0.5 bg-vibrant-orange rounded-full opacity-60 animate-rocket-float"
                  style={{
                    left: `${20 + Math.random() * 60}%`,
                    top: `${30 + Math.random() * 40}%`,
                    animationDelay: `${Math.random() * 3}s`,
                    animationDuration: `${3 + Math.random() * 4}s`,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorMessage; 